import React from 'react';
import type { NavPage } from '../utils/routes';
import Header from './Header';
import { AuthWall } from './AuthState';
import { T } from '../designTokens';
import { useCreditInfo } from '../hooks/useCreditInfo';

interface AuthGatedPageProps {
  /** Which nav item Header highlights as active — a subset of its own currentPage union. */
  headerPage: NavPage;
  navigateTo: (page: NavPage) => void;
  queueCount: number;
  authEnabled: boolean;
  creditInfo: ReturnType<typeof useCreditInfo>;
  onBillingClick: () => void;
  isSignedIn: boolean;
  authWallTitle: string;
  authWallMessage: string;
  /** Gates `children` behind `isAdmin` too, once the auth wall above already passed. */
  requireAdmin?: boolean;
  isAdmin?: boolean;
  adminWallTitle?: string;
  adminWallMessage?: string;
  backgroundColor?: string;
  children: React.ReactNode;
}

// A page shell shared by six of App.tsx's currentPage routes (#/queue,
// #/billing, #/reports, and the three /admin/* routes) — each rendered the
// identical Header + auth-gate + <main> structure with only the header's
// active tab, the auth-wall copy, the optional admin gate, and the gated
// content itself differing (issue #416).
export default function AuthGatedPage({
  headerPage,
  navigateTo,
  queueCount,
  authEnabled,
  creditInfo,
  onBillingClick,
  isSignedIn,
  authWallTitle,
  authWallMessage,
  requireAdmin,
  isAdmin,
  adminWallTitle = 'Admin Access Required',
  adminWallMessage,
  backgroundColor = T.gray50,
  children,
}: AuthGatedPageProps) {
  return (
    <div style={{ minHeight: '100dvh', backgroundColor }}>
      <Header
        currentPage={headerPage}
        onNavigate={navigateTo}
        queueCount={queueCount}
        authEnabled={authEnabled}
        creditBalance={creditInfo.balance}
        creditTier={creditInfo.tier}
        creditLoading={creditInfo.loading}
        creditHistory={creditInfo.hasCreditHistory}
        onBillingClick={onBillingClick}
      />
      <main className="dashboard-container">
        {authEnabled && !isSignedIn ? (
          <AuthWall title={authWallTitle} message={authWallMessage} />
        ) : requireAdmin && !isAdmin ? (
          <AuthWall title={adminWallTitle} message={adminWallMessage ?? 'You need admin privileges to view this page.'} />
        ) : (
          children
        )}
      </main>
    </div>
  );
}
